import { api } from '../api';
import type {
  AssignableEntityRow,
  AuditLogRow,
  OffsetPaginated,
  PrivacyOperationRow,
  EntityOperationRow,
  NotificationRow,
  NotificationRuleRow,
  RoleAssignmentRow,
  RoleRow,
  TenantUserRow,
  WhatsappChannelRow,
  WhatsappTemplateRow,
  WhatsappWhitelistRow,
} from '@/types/domain';

function normalizeNotificationRule(row: NotificationRuleRow): NotificationRuleRow {
  const channels = Array.isArray(row.channels) ? row.channels : [];
  return {
    ...row,
    channels: channels.map((channel) => String(channel)),
    audiencePayload: (row.audiencePayload ?? { type: 'global' }) as Record<string, unknown>,
    filterPayload: (row.filterPayload ?? {}) as Record<string, unknown>,
  };
}

function normalizeWhatsappChannel(raw: Record<string, unknown>): WhatsappChannelRow {
  const healthOk = raw.lastHealthCheckOk;
  let healthStatus = 'unknown';
  if (healthOk === true) healthStatus = 'ok';
  else if (healthOk === false) healthStatus = 'error';
  else if (typeof raw.healthStatus === 'string') healthStatus = raw.healthStatus;

  return {
    id: Number(raw.id),
    phone: String(raw.phoneE164 ?? raw.phone ?? ''),
    displayName: typeof raw.displayName === 'string' ? raw.displayName : null,
    provider: String(raw.providerKind ?? raw.provider ?? ''),
    healthStatus,
    active: String(raw.status ?? 'active') !== 'paused',
  };
}

export const notificationsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    listNotifications: builder.query<
      OffsetPaginated<NotificationRow> & { unreadCount?: number; nextCursor?: number | null },
      { read?: boolean; type?: string; from?: string; limit?: number; offset?: number }
    >({
      query: (params) => {
        const search = new URLSearchParams();
        if (params.read !== undefined) search.set('read', String(params.read));
        if (params.type) search.set('type', params.type);
        if (params.from) search.set('from', params.from);
        if (params.limit) search.set('limit', String(params.limit));
        if (params.offset) search.set('offset', String(params.offset));
        const qs = search.toString();
        return qs ? `/notifications?${qs}` : '/notifications';
      },
      providesTags: [{ type: 'Notification', id: 'LIST' }],
    }),
    markNotificationRead: builder.mutation<void, number>({
      query: (id) => ({ url: `/notifications/${id}/read`, method: 'PATCH' }),
      invalidatesTags: [{ type: 'Notification', id: 'LIST' }],
    }),
    markAllNotificationsRead: builder.mutation<void, void>({
      query: () => ({ url: '/notifications/mark-all-read', method: 'POST' }),
      invalidatesTags: [{ type: 'Notification', id: 'LIST' }],
    }),
    deleteNotification: builder.mutation<void, number>({
      query: (id) => ({ url: `/notifications/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Notification', id: 'LIST' }],
    }),
  }),
});

export const notificationRulesApi = api.injectEndpoints({
  endpoints: (builder) => ({
    listNotificationRules: builder.query<NotificationRuleRow[], void>({
      query: () => '/notification-rules',
      transformResponse: (response: NotificationRuleRow[]) => response.map(normalizeNotificationRule),
      providesTags: [{ type: 'NotificationRule', id: 'LIST' }],
    }),
    getNotificationRule: builder.query<NotificationRuleRow, number>({
      query: (id) => `/notification-rules/${id}`,
      transformResponse: (response: NotificationRuleRow) => normalizeNotificationRule(response),
      providesTags: (_r, _e, id) => [{ type: 'NotificationRule', id }],
    }),
    createNotificationRule: builder.mutation<NotificationRuleRow, Record<string, unknown>>({
      query: (body) => ({ url: '/notification-rules', method: 'POST', body }),
      transformResponse: (response: NotificationRuleRow) => normalizeNotificationRule(response),
      invalidatesTags: [{ type: 'NotificationRule', id: 'LIST' }],
    }),
    updateNotificationRule: builder.mutation<NotificationRuleRow, { id: number; body: Record<string, unknown> }>({
      query: ({ id, body }) => ({ url: `/notification-rules/${id}`, method: 'PATCH', body }),
      transformResponse: (response: NotificationRuleRow) => normalizeNotificationRule(response),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'NotificationRule', id }, { type: 'NotificationRule', id: 'LIST' }],
    }),
    deleteNotificationRule: builder.mutation<void, number>({
      query: (id) => ({ url: `/notification-rules/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'NotificationRule', id: 'LIST' }],
    }),
  }),
});

export const usersApi = api.injectEndpoints({
  endpoints: (builder) => ({
    listTenantUsers: builder.query<TenantUserRow[], void>({
      query: () => '/tenant-users',
      providesTags: [{ type: 'TenantUser', id: 'LIST' }],
    }),
    inviteTenantUser: builder.mutation<TenantUserRow, Record<string, unknown>>({
      query: (body) => ({ url: '/tenant-users', method: 'POST', body }),
      invalidatesTags: [{ type: 'TenantUser', id: 'LIST' }],
    }),
    updateTenantUser: builder.mutation<TenantUserRow, { id: number; body: Record<string, unknown> }>({
      query: ({ id, body }) => ({ url: `/tenant-users/${id}`, method: 'PATCH', body }),
      invalidatesTags: [{ type: 'TenantUser', id: 'LIST' }],
    }),
    getAssignableEntities: builder.query<
      AssignableEntityRow[],
      { entityTypeIds?: number[]; contextId?: number; q?: string }
    >({
      query: (params) => {
        const search = new URLSearchParams();
        if (params.entityTypeIds?.length) {
          search.set('entityTypeIds', params.entityTypeIds.join(','));
        }
        if (params.contextId) search.set('contextId', String(params.contextId));
        if (params.q) search.set('q', params.q.slice(0, 128));
        const qs = search.toString();
        return qs ? `/tenant-users/assignable-entities?${qs}` : '/tenant-users/assignable-entities';
      },
    }),
  }),
});

export const rolesApi = api.injectEndpoints({
  endpoints: (builder) => ({
    listRoles: builder.query<RoleRow[], void>({
      query: () => '/roles',
      providesTags: [{ type: 'Role', id: 'LIST' }],
    }),
    getRole: builder.query<RoleRow, number>({
      query: (id) => `/roles/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Role', id }],
    }),
    getRoleActionsCatalog: builder.query<unknown[], void>({
      query: () => '/roles/catalog/actions',
    }),
    createRole: builder.mutation<RoleRow, Record<string, unknown>>({
      query: (body) => ({ url: '/roles', method: 'POST', body }),
      invalidatesTags: [{ type: 'Role', id: 'LIST' }],
    }),
    updateRole: builder.mutation<RoleRow, { id: number; body: Record<string, unknown> }>({
      query: ({ id, body }) => ({ url: `/roles/${id}`, method: 'PATCH', body }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Role', id }, { type: 'Role', id: 'LIST' }],
    }),
    deleteRole: builder.mutation<void, number>({
      query: (id) => ({ url: `/roles/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Role', id: 'LIST' }],
    }),
  }),
});

export const roleAssignmentsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    listRoleAssignments: builder.query<RoleAssignmentRow[], number>({
      query: (userId) => `/users/${userId}/role-assignments`,
      providesTags: (_r, _e, userId) => [{ type: 'RoleAssignment', id: userId }],
    }),
    createRoleAssignment: builder.mutation<
      RoleAssignmentRow,
      { userId: number; body: Record<string, unknown> }
    >({
      query: ({ userId, body }) => ({
        url: `/users/${userId}/role-assignments`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { userId }) => [{ type: 'RoleAssignment', id: userId }],
    }),
    revokeRoleAssignment: builder.mutation<void, { userId: number; assignmentId: number; reason?: string }>({
      query: ({ userId, assignmentId, reason }) => ({
        url: `/users/${userId}/role-assignments/${assignmentId}`,
        method: 'DELETE',
        body: reason ? { reason } : undefined,
      }),
      invalidatesTags: (_r, _e, { userId }) => [{ type: 'RoleAssignment', id: userId }],
    }),
  }),
});

export const whatsappApi = api.injectEndpoints({
  endpoints: (builder) => ({
    listWhatsappChannels: builder.query<WhatsappChannelRow[], void>({
      query: () => '/admin/whatsapp/channels',
      transformResponse: (response: Record<string, unknown>[]) =>
        response.map((row) => normalizeWhatsappChannel(row)),
      providesTags: [{ type: 'WhatsappChannel', id: 'LIST' }],
    }),
    getWhatsappChannel: builder.query<WhatsappChannelRow, number>({
      query: (id) => `/admin/whatsapp/channels/${id}`,
      transformResponse: (response: Record<string, unknown>) => normalizeWhatsappChannel(response),
      providesTags: (_r, _e, id) => [{ type: 'WhatsappChannel', id }],
    }),
    createWhatsappChannel: builder.mutation<WhatsappChannelRow, Record<string, unknown>>({
      query: (body) => ({ url: '/admin/whatsapp/channels', method: 'POST', body }),
      transformResponse: (response: Record<string, unknown>) => normalizeWhatsappChannel(response),
      invalidatesTags: [{ type: 'WhatsappChannel', id: 'LIST' }],
    }),
    updateWhatsappChannel: builder.mutation<WhatsappChannelRow, { id: number; body: Record<string, unknown> }>({
      query: ({ id, body }) => ({ url: `/admin/whatsapp/channels/${id}`, method: 'PATCH', body }),
      transformResponse: (response: Record<string, unknown>) => normalizeWhatsappChannel(response),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'WhatsappChannel', id }, { type: 'WhatsappChannel', id: 'LIST' }],
    }),
    rotateWhatsappSecret: builder.mutation<unknown, { id: number; webhookSecret?: string }>({
      query: ({ id, webhookSecret }) => ({
        url: `/admin/whatsapp/channels/${id}/rotate-secret`,
        method: 'POST',
        body: webhookSecret ? { webhookSecret } : {},
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'WhatsappChannel', id }],
    }),
    runWhatsappHealthCheck: builder.mutation<unknown, number>({
      query: (id) => ({ url: `/admin/whatsapp/channels/${id}/health-check`, method: 'POST' }),
      invalidatesTags: (_r, _e, id) => [{ type: 'WhatsappChannel', id }],
    }),
    sendWhatsappTestMessage: builder.mutation<unknown, { id: number; body: Record<string, unknown> }>({
      query: ({ id, body }) => ({
        url: `/admin/whatsapp/channels/${id}/test-message`,
        method: 'POST',
        body,
      }),
    }),
    listWhatsappTemplates: builder.query<WhatsappTemplateRow[], number>({
      query: (id) => `/admin/whatsapp/channels/${id}/templates`,
      providesTags: (_r, _e, id) => [{ type: 'WhatsappChannel', id: `templates-${id}` }],
    }),
    syncWhatsappTemplates: builder.mutation<WhatsappTemplateRow[], number>({
      query: (id) => ({ url: `/admin/whatsapp/channels/${id}/templates/sync`, method: 'POST' }),
      invalidatesTags: (_r, _e, id) => [{ type: 'WhatsappChannel', id: `templates-${id}` }],
    }),
    listWhatsappWhitelist: builder.query<WhatsappWhitelistRow[], number>({
      query: (id) => `/admin/whatsapp/channels/${id}/whitelist`,
      providesTags: (_r, _e, id) => [{ type: 'WhatsappChannel', id: `whitelist-${id}` }],
    }),
    addWhatsappWhitelist: builder.mutation<
      WhatsappWhitelistRow,
      { channelId: number; phoneE164: string; label?: string }
    >({
      query: ({ channelId, ...body }) => ({
        url: `/admin/whatsapp/channels/${channelId}/whitelist`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { channelId }) => [{ type: 'WhatsappChannel', id: `whitelist-${channelId}` }],
    }),
    removeWhatsappWhitelist: builder.mutation<void, { channelId: number; phoneId: number }>({
      query: ({ channelId, phoneId }) => ({
        url: `/admin/whatsapp/channels/${channelId}/whitelist/${phoneId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { channelId }) => [{ type: 'WhatsappChannel', id: `whitelist-${channelId}` }],
    }),
  }),
});

export const auditLogsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    listAuditLogs: builder.query<
      OffsetPaginated<AuditLogRow>,
      {
        limit?: number;
        offset?: number;
        resourceType?: string;
        resourceId?: string;
        userId?: number;
        action?: string;
        from?: string;
        to?: string;
      }
    >({
      query: (params) => {
        const search = new URLSearchParams();
        if (params.limit) search.set('limit', String(params.limit));
        if (params.offset) search.set('offset', String(params.offset));
        if (params.resourceType) search.set('resourceType', params.resourceType.slice(0, 64));
        if (params.resourceId) search.set('resourceId', params.resourceId.slice(0, 64));
        if (params.userId) search.set('userId', String(params.userId));
        if (params.action) search.set('action', params.action.slice(0, 64));
        if (params.from) search.set('from', params.from.slice(0, 32));
        if (params.to) search.set('to', params.to.slice(0, 32));
        const qs = search.toString();
        return qs ? `/admin/audit/logs?${qs}` : '/admin/audit/logs';
      },
      providesTags: [{ type: 'AuditLog', id: 'LIST' }],
    }),
  }),
});

export const privacyApi = api.injectEndpoints({
  endpoints: (builder) => ({
    erasePrivacyData: builder.mutation<unknown, { entityId: number; reason: string }>({
      query: (body) => ({ url: '/admin/privacy/erase', method: 'POST', body }),
    }),
    listPrivacyOperations: builder.query<PrivacyOperationRow[], { entityId?: number } | void>({
      query: (params) => {
        if (params?.entityId) {
          return `/admin/privacy/operations?entityId=${params.entityId}`;
        }
        return '/admin/privacy/operations';
      },
    }),
  }),
});

export const entityOperationsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    listEntityOperations: builder.query<
      EntityOperationRow[],
      { entityId?: number; status?: string; operationType?: string }
    >({
      query: (params) => {
        const search = new URLSearchParams();
        if (params.entityId) search.set('entityId', String(params.entityId));
        if (params.status) search.set('status', params.status);
        if (params.operationType) search.set('operationType', params.operationType);
        const qs = search.toString();
        return qs ? `/entity-operations?${qs}` : '/entity-operations';
      },
      providesTags: [{ type: 'EntityOperation', id: 'LIST' }],
    }),
    getEntityOperation: builder.query<EntityOperationRow, number>({
      query: (id) => `/entity-operations/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'EntityOperation', id }],
    }),
    createEntityOperation: builder.mutation<EntityOperationRow, Record<string, unknown>>({
      query: (body) => ({ url: '/entity-operations', method: 'POST', body }),
      invalidatesTags: [{ type: 'EntityOperation', id: 'LIST' }],
    }),
    resolveEntityOperation: builder.mutation<
      EntityOperationRow,
      { id: number; action?: 'approve' | 'reject'; comment?: string }
    >({
      query: ({ id, ...body }) => ({ url: `/entity-operations/${id}/resolve`, method: 'PATCH', body }),
      invalidatesTags: [{ type: 'EntityOperation', id: 'LIST' }],
    }),
  }),
});

export const {
  useListNotificationsQuery,
  useMarkNotificationReadMutation,
  useMarkAllNotificationsReadMutation,
  useDeleteNotificationMutation,
} = notificationsApi;

export const {
  useListNotificationRulesQuery,
  useGetNotificationRuleQuery,
  useCreateNotificationRuleMutation,
  useUpdateNotificationRuleMutation,
  useDeleteNotificationRuleMutation,
} = notificationRulesApi;

export const {
  useListTenantUsersQuery,
  useInviteTenantUserMutation,
  useUpdateTenantUserMutation,
  useGetAssignableEntitiesQuery,
} = usersApi;

export const {
  useListRolesQuery,
  useGetRoleQuery,
  useGetRoleActionsCatalogQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
} = rolesApi;

export const {
  useListRoleAssignmentsQuery,
  useCreateRoleAssignmentMutation,
  useRevokeRoleAssignmentMutation,
} = roleAssignmentsApi;

export const {
  useListWhatsappChannelsQuery,
  useGetWhatsappChannelQuery,
  useCreateWhatsappChannelMutation,
  useUpdateWhatsappChannelMutation,
  useRotateWhatsappSecretMutation,
  useRunWhatsappHealthCheckMutation,
  useSendWhatsappTestMessageMutation,
  useListWhatsappTemplatesQuery,
  useSyncWhatsappTemplatesMutation,
  useListWhatsappWhitelistQuery,
  useAddWhatsappWhitelistMutation,
  useRemoveWhatsappWhitelistMutation,
} = whatsappApi;

export const { useListAuditLogsQuery } = auditLogsApi;

export const {
  useErasePrivacyDataMutation,
  useListPrivacyOperationsQuery,
} = privacyApi;

export const {
  useListEntityOperationsQuery,
  useGetEntityOperationQuery,
  useCreateEntityOperationMutation,
  useResolveEntityOperationMutation,
} = entityOperationsApi;
